import { ComponentPropsWithoutRef, ComponentType, FocusEventHandler, ReactNode, useState } from 'react';
import styled from 'styled-components';

import { Box } from '../../../common/ui/Box';
import { theme } from '../../../common/ui/theme';
import { Caption, CaptionProps } from '../../Caption/Caption';
import { Text } from '../../Text/Text';

import { BaseInput } from './BaseInput';
import { InputContainer } from './InputContainer';
import { InputLabelText } from './InputLabelText';
import { LeadingVisualWrapper } from './LeadingVisualWrapper';
import { Optional } from './Optional';
import { Required } from './Required';
import { TrailingVisualWrapper } from './TrailingVisualWrapper';

export interface CoreInputProps extends Pick<ComponentPropsWithoutRef<'input'>, 'disabled' | 'onFocus' | 'onBlur'> {
    /**
     * Define a label shown above the input
     */
    label?: string;
    /**
     * Define a helper text or caption for additional information below the input
     */
    caption?: string;
    /**
     * Define a helper icon for additional information below the input
     */
    CaptionIcon?: CaptionProps['Icon'];
    /**
     * Define additional information or actions on the top right of the input
     */
    meta?: ReactNode;
    /**
     * Add styles indicating a positive user input
     */
    positive?: boolean;
    /**
     * Add styles indicating a negative or destructive user input
     */
    negative?: boolean;
    /**
     * Add an element at the beginning of the input
     */
    leadingVisual?: ReactNode;
    /**
     * Add an element at the end of the input
     */
    trailingVisual?: ReactNode;
    /**
     * Override the base input component
     */
    as?: string | ComponentType<any>;
    /**
     * Add required indicator to label
     */
    markAsRequired?: boolean;
    /**
     * Add optional indicator to label
     */
    markAsOptional?: boolean;
    /**
     * Add a description below the label
     */
    description?: string;
}

export const InputLabel = styled.label`
    display: inline-block;
    width: 100%;

    // required to override global styles
    margin-bottom: 0;
    font-weight: ${theme.fontWeights[500]};
`;

export const CoreInput = ({
    label,
    caption,
    CaptionIcon,
    meta,
    positive,
    negative,
    disabled,
    leadingVisual,
    trailingVisual,
    markAsRequired,
    markAsOptional,
    description,
    ...rest
}: CoreInputProps) => {
    const [isInputFocused, setIsInputFocused] = useState(false);

    const handleFocus: FocusEventHandler<HTMLInputElement> = (event) => {
        setIsInputFocused(true);
        rest.onFocus && rest.onFocus(event);
    };

    const handleBlur: FocusEventHandler<HTMLInputElement> = (event) => {
        setIsInputFocused(false);
        rest.onBlur && rest.onBlur(event);
    };

    return (
        <InputLabel>
            <Box display="flex" flexDirection="column" gap={4}>
                {label || meta ? (
                    <Box display="flex" justifyContent="space-between">
                        {label ? (
                            <InputLabelText disabled={disabled}>
                                {label}
                                {markAsRequired ? <Required disabled={disabled} /> : null}
                                {markAsOptional ? <Optional disabled={disabled} /> : null}
                            </InputLabelText>
                        ) : null}
                        {meta ? <InputLabelText disabled={disabled}>{meta}</InputLabelText> : null}
                    </Box>
                ) : null}

                {description ? (
                    <Text variant="extraSmall" color="foreground.muted">
                        {description}
                    </Text>
                ) : null}

                <InputContainer
                    $positive={positive}
                    $negative={negative}
                    $isFocused={isInputFocused}
                    disabled={disabled}
                >
                    {leadingVisual ? (
                        <LeadingVisualWrapper disabled={disabled}>{leadingVisual}</LeadingVisualWrapper>
                    ) : null}

                    <BaseInput
                        {...rest}
                        disabled={disabled}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        $hasLeadingVisual={!!leadingVisual}
                        $hasTrailingVisual={!!trailingVisual}
                    />

                    {trailingVisual ? (
                        <TrailingVisualWrapper disabled={disabled}>{trailingVisual}</TrailingVisualWrapper>
                    ) : null}
                </InputContainer>

                {caption ? (
                    <Caption $negative={negative} Icon={CaptionIcon}>
                        {caption}
                    </Caption>
                ) : null}
            </Box>
        </InputLabel>
    );
};
